import { IPromise } from 'angular';
import * as React from 'react';

import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import { Formatters, Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { L10n } from '../../../ts/L10n/L10n';
import { ModalInstance, ReactModalOptions, ReactModalService } from '../../../ts/services/ReactModalService';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { ServiceLocator } from '../../ServiceLocator';
import { CollectionsResourceQuery } from './Collections';

const DIALOG_WIDTH = 400;

export enum DeleteCollectionDialogCloseReason {
  Delete = 'Delete',
  Cancel = 'Cancel',
}

export namespace DeleteCollectionDialog {
  export interface ContentsProps {
    readonly close: (reason: DeleteCollectionDialogCloseReason) => void;
    readonly id: string;
    readonly name: string;
  }

  export class Contents extends React.PureComponent<ContentsProps> {
    public render(): JSX.Element {
      return (
        <>
          {this.getMessage()}
          {this.buttonRow()}
        </>
      );
    }

    private getMessage = (): string => {
      let body = '';
      body += Messages.confirmDeleteContent({
        CONTENT_NAME: L10n.elideIfLong(this.props.name),
        CONTENT_TYPE: L10n.elideIfLong(Messages.collection())
      });
      body += ' ';
      body += Messages.deleteCollectionWarning();
      return body;
    }

    private buttonRow(): JSX.Element {
      return (
        <div style={{ marginTop: 30, width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={'cancel-delete'}
            onClick={this.cancelClick}
          >
            {Messages.cancel()}
          </ButtonWidget>
          <span className="tb-margin-left-wide">
            <ButtonWidget
              buttonType={ButtonType.Destructive}
              testId={'delete-collection'}
              onClick={this.handleDeleteButtonClick}
            >
              {Messages.deleteMessage()}
            </ButtonWidget>
          </span>
        </div>
      );
    }

    private cancelClick = () => {
      this.props.close(DeleteCollectionDialogCloseReason.Cancel);
    }

    private handleSuccessfulDeletion = () => {
      const deleteCount = 1;
      this.props.close(DeleteCollectionDialogCloseReason.Delete);

      const toaster: ToasterService = ServiceLocator.get<ToasterService>('ToasterService');
      toaster.markToastsAsDoNotDismissOnLocationChange();

      toaster.pop({
        type: ToastType.Info,
        text: Messages.deleteCollectionsSuccess({
          ITEM_COUNT: deleteCount,
          ITEM_COUNT_LOC: Formatters.formatCount(deleteCount),
        })
      });
    }

    private handleDeleteButtonClick = (): void => {
      (new CollectionsResourceQuery()).delete(this.props.id)
        .then(() => {
          this.handleSuccessfulDeletion();
        });
    }
  }

  export function Options(closeDialog: (reason: DeleteCollectionDialogCloseReason) => void, id: string, name: string): ReactModalOptions {
    return {
      title: Messages.deleteMessage(),
      testId: 'delete-collection-dialog',
      type: DialogType.LightweightTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
      dialogContent: <Contents close={closeDialog} id={id} name={name}/>,
      dialogContentStyle: { padding: CommonStyles.Sizes.MediumSpace },
    };
  }

  export function open(id: string, name: string): IPromise<any> {
    let modalInstance: ModalInstance;

    const closeDialog = (reason: DeleteCollectionDialogCloseReason) => modalInstance && modalInstance.close(reason);

    modalInstance = ReactModalService.open(Options(closeDialog, id, name));
    return modalInstance.result;
  }
}
